import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { FileText, Lock, Download, AlertTriangle, Shield, Eye, X } from 'lucide-react'
import EmailCaptureGate from '../components/EmailCaptureGate'
// NDA handling removed - now handled at login time
import { useAuditLog } from '../hooks/useAuditLog'
import { useTenant } from '../contexts/TenantContext'

interface DocumentMetadata {
  id: string
  title: string
  category: string
  documentType?: string
  attestationTags?: string[]
  size: number
  mimeType: string
  uploadedAt: string
  visibility: 'public' | 'gated' | 'admin'
  gated: boolean
  ndaRequired: boolean
  watermarked: boolean
  redacted?: boolean
  maxPages?: number // Deprecated
  allowedPages?: string // New format: "5" or "1,3,5" or "1-5,10"
}

export default function DocumentViewPage() {
  const { documentId } = useParams<{ documentId: string }>()
  const navigate = useNavigate()
  const { currentTenant } = useTenant()
  const { logEvent } = useAuditLog()
  const [document, setDocument] = useState<DocumentMetadata | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasAccess, setHasAccess] = useState(false)
  const [userRole, setUserRole] = useState<string>('visitor')
  const [downloading, setDownloading] = useState(false)
  const [showEmailGate, setShowEmailGate] = useState(false)
  const [emailSubmitted, setEmailSubmitted] = useState(false)
  const [userEmail, setUserEmail] = useState<string>('')
  const [userCompany, setUserCompany] = useState<string>('')
  // NDA handling removed - now handled at login time
  const [showViewer, setShowViewer] = useState(false)
  const [viewerWatermarkText, setViewerWatermarkText] = useState('')
  const [isSpacebarHeld, setIsSpacebarHeld] = useState(false)
  const [spacebarHoldStartTime, setSpacebarHoldStartTime] = useState<number | null>(null)
  const [mobileRevealTimer, setMobileRevealTimer] = useState<NodeJS.Timeout | null>(null)
  const [mobileRevealTimeLeft, setMobileRevealTimeLeft] = useState(0)

  useEffect(() => {
    if (currentTenant || sessionStorage.getItem('tenant-id')) {
      fetchDocument()
    }
    checkAccess()
  }, [documentId, currentTenant])

  // Generate dynamic watermark with user info when viewer opens
  useEffect(() => {
    if (showViewer) {
      const now = new Date()
      const timestamp = now.toLocaleString()
      const userIdentifier = userEmail || 'visitor'
      setViewerWatermarkText(`${userIdentifier} • ${timestamp}`)

      // Log document view event
      logEvent({
        eventType: 'document_view',
        userEmail: userEmail || undefined,
        resourceType: 'document',
        resourceId: documentId,
        resourceName: document?.title,
        metadata: {
          viewStartTime: timestamp,
          redacted: document?.redacted || false
        },
        severity: 'info'
      })

      // Detect visibility changes (possible screen recording/capture)
      const handleVisibilityChange = () => {
        if (document.hidden) {
          console.warn('[Document Viewer] Tab hidden - possible screen capture')
          logEvent({
            eventType: 'document_view_interrupted',
            userEmail: userEmail || undefined,
            resourceType: 'document',
            resourceId: documentId,
            resourceName: document?.title,
            metadata: {
              reason: 'visibility_change',
              timestamp: new Date().toISOString()
            },
            severity: 'warning'
          })
        }
      }

      // Detect focus loss (switching apps - possible screenshot tool)
      const handleBlur = () => {
        console.warn('[Document Viewer] Window lost focus - possible screen capture')
        logEvent({
          eventType: 'document_view_suspicious',
          userEmail: userEmail || undefined,
          resourceType: 'document',
          resourceId: documentId,
          resourceName: document?.title,
          metadata: {
            reason: 'focus_loss',
            timestamp: new Date().toISOString()
          },
          severity: 'warning'
        })
      }

      // Detect keyboard shortcuts commonly used for screenshots
      const handleKeyDown = (e: KeyboardEvent) => {
        // macOS: Cmd+Shift+3, Cmd+Shift+4, Cmd+Shift+5
        // Windows: Windows+Shift+S, PrtScn
        if (
          (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) ||
          (e.key === 'PrintScreen') ||
          (e.metaKey && e.shiftKey && e.key === 's')
        ) {
          console.warn('[Document Viewer] Screenshot keyboard shortcut detected')
          logEvent({
            eventType: 'document_screenshot_attempt',
            userEmail: userEmail || undefined,
            resourceType: 'document',
            resourceId: documentId,
            resourceName: document?.title,
            metadata: {
              keyCombo: `${e.metaKey ? 'Meta+' : ''}${e.shiftKey ? 'Shift+' : ''}${e.key}`,
              timestamp: new Date().toISOString()
            },
            severity: 'warning'
          })
        }

        // Handle spacebar for redacted documents (external users only)
        const isExternalUser = userRole !== 'admin' && userRole !== 'owner'
        if (document?.redacted && isExternalUser && e.code === 'Space' && !e.repeat) {
          e.preventDefault()
          setIsSpacebarHeld(true)
          setSpacebarHoldStartTime(Date.now())
          console.log('[Document Viewer] Spacebar pressed - revealing content')
        }
      }

      const handleKeyUp = (e: KeyboardEvent) => {
        // Handle spacebar release for redacted documents (external users only)
        const isExternalUser = userRole !== 'admin' && userRole !== 'owner'
        if (document?.redacted && isExternalUser && e.code === 'Space') {
          e.preventDefault()
          setIsSpacebarHeld(false)

          if (spacebarHoldStartTime) {
            const holdDuration = Date.now() - spacebarHoldStartTime
            console.log(`[Document Viewer] Spacebar released - content hidden (held for ${holdDuration}ms)`)

            // Log hold event to audit trail
            logEvent({
              eventType: 'document_spacebar_reveal',
              userEmail: userEmail || undefined,
              resourceType: 'document',
              resourceId: documentId,
              resourceName: document?.title,
              metadata: {
                holdDurationMs: holdDuration,
                timestamp: new Date().toISOString()
              },
              severity: 'info'
            })

            setSpacebarHoldStartTime(null)
          }
        }
      }

      window.document.addEventListener('visibilitychange', handleVisibilityChange)
      window.addEventListener('blur', handleBlur)
      window.document.addEventListener('keydown', handleKeyDown)
      window.document.addEventListener('keyup', handleKeyUp)

      return () => {
        window.document.removeEventListener('visibilitychange', handleVisibilityChange)
        window.removeEventListener('blur', handleBlur)
        window.document.removeEventListener('keydown', handleKeyDown)
        window.document.removeEventListener('keyup', handleKeyUp)

        // Clean up mobile reveal timer if component unmounts
        if (mobileRevealTimer) {
          clearTimeout(mobileRevealTimer)
          setMobileRevealTimer(null)
          setMobileRevealTimeLeft(0)
        }
      }
    }
  }, [showViewer, userEmail, documentId, document, mobileRevealTimer, userRole])

  const fetchDocument = async () => {
    try {
      // Get tenant ID from context or sessionStorage
      const tenantId = currentTenant?.id || sessionStorage.getItem('tenant-id')
      const version = sessionStorage.getItem('content-version') || 'live'
      const token = localStorage.getItem('auth-token')

      console.log('[DocumentViewPage] fetchDocument:', { tenantId, version, hasToken: !!token, documentId })

      const headers: HeadersInit = {}

      // Add auth token if available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // Add tenant headers for external users
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId
        headers['X-Content-Version'] = version
      }

      // Use /api/documents for all cases (works for both authenticated and unauthenticated)
      const endpoint = '/api/documents'

      console.log('[DocumentViewPage] Fetching from:', endpoint)

      const response = await fetch(endpoint, {
        credentials: 'include',
        headers
      })

      console.log('[DocumentViewPage] Response:', { ok: response.ok, status: response.status })

      if (!response.ok) throw new Error('Failed to fetch documents')

      const documents = await response.json()
      console.log('[DocumentViewPage] Found documents:', documents.length)

      const doc = documents.find((d: any) => d.id === documentId)

      if (!doc) {
        console.error('[DocumentViewPage] Document not found in list. DocumentId:', documentId)
        setError('Document not found')
        return
      }

      console.log('[DocumentViewPage] Found document:', doc.title)
      setDocument(doc)
    } catch (err) {
      console.error('[DocumentViewPage] Error:', err)
      setError('Failed to load document')
    } finally {
      setLoading(false)
    }
  }

  const checkAccess = async () => {
    try {
      const response = await fetch('/api/auth/validate', {
        credentials: 'include'
      })
      if (response.ok) {
        const data = await response.json()
        setHasAccess(true)
        setUserRole(data.user?.role || 'visitor')

        // Store user email for later NDA checks
        if (data.user?.email) {
          setUserEmail(data.user.email)
        }
      } else {
        setHasAccess(false)
        setUserRole('visitor')
      }
    } catch (err) {
      console.error('Access check failed:', err)
      setHasAccess(false)
      setUserRole('visitor')
    }
  }

  const handleEmailSubmit = (email: string, company: string) => {
    setEmailSubmitted(true)
    setShowEmailGate(false)
    setUserEmail(email)
    setUserCompany(company)

    // Log email capture event
    logEvent({
      eventType: 'email_captured',
      userEmail: email,
      userCompany: company,
      resourceType: 'document',
      resourceId: documentId,
      resourceName: document?.title,
      severity: 'info'
    })

    // NDA handling removed - now handled at login time
    // Proceed directly to download
    performDownload()
  }

  const handleDownload = async () => {
    if (!document || !documentId) return

    // Check if this is a public document that needs email capture
    if (document.visibility === 'public' && !emailSubmitted && !hasAccess) {
      setShowEmailGate(true)
      return
    }

    // If email was already submitted or user has access, proceed with download
    performDownload()
  }

  const performDownload = async () => {
    if (!document || !documentId) return

    setDownloading(true)
    let url: string | null = null
    try {
      // Log document download event
      logEvent({
        eventType: 'document_download',
        userEmail: userEmail || undefined,
        resourceType: 'document',
        resourceId: documentId,
        resourceName: document.title,
        metadata: {
          category: document.category,
          gated: document.gated,
          watermarked: document.watermarked
        },
        severity: 'info'
      })

      // Send Slack notification
      try {
        await fetch('/api/slack-notify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId: sessionStorage.getItem('tenant-id'),
            eventType: 'document_download',
            userEmail: userEmail || undefined,
            resourceName: document.title,
            metadata: {
              category: document.category
            }
          })
        })
      } catch (e) {
        // Slack notification failure shouldn't block download
        console.warn('Failed to send Slack notification:', e)
      }

      const response = await fetch(`/api/download-document?id=${documentId}`, {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to download document')
      }

      const blob = await response.blob()
      url = window.URL.createObjectURL(blob)
      const a = window.document.createElement('a')
      a.href = url
      a.download = document.title
      window.document.body.appendChild(a)
      a.click()
      window.document.body.removeChild(a)
    } catch (err) {
      alert('Failed to download document. Please try again.')
    } finally {
      if (url) {
        window.URL.revokeObjectURL(url)
      }
      setDownloading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading document...</p>
        </div>
      </div>
    )
  }

  if (error || !document) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Document Not Found</h1>
          <p className="text-gray-600 mb-6">{error || 'The requested document could not be found.'}</p>
          <button
            onClick={() => navigate('/evidence')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Return to Trust Center
          </button>
        </div>
      </div>
    )
  }

  // Admin-only documents
  if (document.visibility === 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <Lock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h1>
          <p className="text-gray-600 mb-6">
            This document is for internal use only and is not publicly accessible.
          </p>
          <button
            onClick={() => navigate('/evidence')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Return to Trust Center
          </button>
        </div>
      </div>
    )
  }

  // Gated documents - require access request
  if (document.gated && !hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-full sm:max-w-2xl w-full">
          <div className="text-center mb-6">
            <Shield className="h-16 w-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{document.title}</h1>
            <p className="text-gray-600">This document requires access approval</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-blue-900 mb-1">Document Details</h3>
                <div className="text-sm text-blue-700 space-y-1">
                  <p><span className="font-medium">Category:</span> {document.category}</p>
                  {document.documentType && (
                    <p><span className="font-medium">Type:</span> {document.documentType}</p>
                  )}
                  {document.attestationTags && document.attestationTags.length > 0 && (
                    <p><span className="font-medium">Attestations:</span> {document.attestationTags.join(', ')}</p>
                  )}
                  <p><span className="font-medium">Size:</span> {(document.size / 1024 / 1024).toFixed(2)} MB</p>
                  {document.ndaRequired && (
                    <p className="text-purple-700 font-medium">⚠️ NDA Required</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-700 mb-6">
              To access this document, please request access through our Trust Center.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate('/evidence')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Request Access
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Return to Trust Center
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Public documents or user has access
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-full sm:max-w-2xl w-full">
        <div className="text-center mb-6">
          <FileText className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{document.title}</h1>
          <p className="text-gray-600">Ready to download</p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Category:</span>
              <p className="text-gray-900">{document.category}</p>
            </div>
            {document.documentType && (
              <div>
                <span className="font-medium text-gray-700">Type:</span>
                <p className="text-gray-900 capitalize">{document.documentType}</p>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-700">Size:</span>
              <p className="text-gray-900">{(document.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Uploaded:</span>
              <p className="text-gray-900">{new Date(document.uploadedAt).toLocaleDateString()}</p>
            </div>
          </div>

          {document.attestationTags && document.attestationTags.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <span className="font-medium text-gray-700 block mb-2">Attestations:</span>
              <div className="flex flex-wrap gap-2">
                {document.attestationTags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {document.watermarked && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-blue-700">
                ℹ️ This document will be watermarked with your access information
              </p>
            </div>
          )}

          {document.redacted && userRole !== 'admin' && userRole !== 'owner' && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-900 font-medium mb-2">
                  ⚠️ Preview Document with Download Restrictions
                </p>
                <div className="text-xs text-amber-800 space-y-1">
                  <p>• <strong>View:</strong> You can view the full document online</p>
                  <p>• <strong>Download:</strong> Limited to {document.allowedPages || document.maxPages?.toString() || '5'} {document.allowedPages?.includes(',') || document.allowedPages?.includes('-') ? '' : '(first pages)'}</p>
                  <p>• Downloads are watermarked with "REDACTED - LIMITED PREVIEW"</p>
                </div>
              </div>
            </div>
          )}

          {/* Compliance Control Mapping - Detailed View */}
          {document.complianceMapping && document.complianceMapping.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-900 mb-4">
                Compliance Framework Mapping
              </h3>

              {document.complianceMapping.map((framework, fIdx) => (
                <div key={fIdx} className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-blue-600" />
                      <h4 className="text-md font-semibold text-gray-800">
                        {framework.framework}
                      </h4>
                      <span className="text-sm text-gray-500">
                        ({framework.controls.length} controls)
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Confidence: {Math.round(framework.totalConfidence * 100)}%
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Control ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Control Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Evidence
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                            Confidence
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {framework.controls.map((control, cIdx) => (
                          <tr key={cIdx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                              {control.id}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {control.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <div className="max-w-md">
                                <p className="line-clamp-2" title={control.evidenceSnippet}>
                                  "{control.evidenceSnippet}"
                                </p>
                                {control.evidenceLocation && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {control.evidenceLocation}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                                  <div
                                    className={`h-2 rounded-full ${
                                      control.confidence >= 0.9
                                        ? 'bg-green-600'
                                        : control.confidence >= 0.7
                                        ? 'bg-yellow-500'
                                        : 'bg-orange-500'
                                    }`}
                                    style={{ width: `${control.confidence * 100}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600 w-10">
                                  {Math.round(control.confidence * 100)}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {document.lastComplianceScan && (
                <p className="text-xs text-gray-500 mt-4">
                  Last scanned: {new Date(document.lastComplianceScan).toLocaleString()}
                  {' '}• Detected by AI analysis
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-center">
          {document.mimeType === 'application/pdf' && (
            <button
              onClick={() => {
                // Check if email capture is needed first
                if (document.visibility === 'public' && !emailSubmitted && !hasAccess) {
                  setShowEmailGate(true)
                  return
                }

                // NDA handling removed - now handled at login time
                // Just show viewer directly
                setShowViewer(true)
              }}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center"
            >
              <Eye className="h-5 w-5 mr-2" />
              View Document
            </button>
          )}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {downloading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Downloading...
              </>
            ) : (
              <>
                <Download className="h-5 w-5 mr-2" />
                {document.redacted && userRole !== 'admin' && userRole !== 'owner' ? 'Download Redacted Copy' : 'Download Document'}
              </>
            )}
          </button>
          <button
            onClick={() => navigate('/evidence')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            Return to Trust Center
          </button>
        </div>
      </div>

      {/* Email Capture Gate */}
      {showEmailGate && document && (
        <EmailCaptureGate
          documentName={document.title}
          onSubmit={handleEmailSubmit}
          onClose={() => setShowEmailGate(false)}
        />
      )}

      {/* NDA Clickwrap removed - now handled at login time */}

      {/* PDF Viewer Modal */}
      {showViewer && document && (
        <>
          {/* Print prevention CSS */}
          <style>{`
            @media print {
              .document-viewer-modal {
                display: none !important;
              }
              body::after {
                content: "This document is protected and cannot be printed. Download the PDF to print.";
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                font-size: 24px;
                color: #f59e0b;
                text-align: center;
              }
            }
          `}</style>
          <div
            className="document-viewer-modal fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
            onContextMenu={(e) => e.preventDefault()}
          >
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">{document.title}</h2>
                {document.redacted && userRole !== 'admin' && userRole !== 'owner' && (
                  <p className="text-sm text-amber-600 mt-1">
                    ⚠️ Full document preview - Download limited to pages: {document.allowedPages || document.maxPages?.toString() || '5'}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowViewer(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close viewer"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            {/* PDF Viewer with protections */}
            <div
              className="flex-1 relative overflow-hidden"
              style={{
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
              onContextMenu={(e) => e.preventDefault()}
            >
              {/* Black redaction overlay - only visible when spacebar NOT held */}
              {document.redacted && userRole !== 'admin' && userRole !== 'owner' && !isSpacebarHeld && (
                <div
                  className="absolute inset-0 bg-black z-20 flex items-center justify-center transition-opacity duration-200"
                  style={{ opacity: 1 }}
                >
                  <div className="text-center px-8 pointer-events-none">
                    <div className="text-white text-4xl font-bold mb-4">
                      🔒 Redacted Document
                    </div>
                    <div className="text-gray-300 text-xl mb-6">
                      <span className="hidden sm:inline">Hold <kbd className="px-4 py-2 bg-gray-700 rounded-lg text-white font-mono text-2xl mx-2 inline-block shadow-lg">SPACE</kbd> to view</span>
                      <span className="sm:hidden">Tap button below to reveal (10s)</span>
                    </div>
                    <div className="text-gray-400 text-sm max-w-md mx-auto mb-6">
                      {mobileRevealTimeLeft > 0 ? (
                        <span className="text-amber-400 font-bold">Content visible for {mobileRevealTimeLeft}s</span>
                      ) : (
                        <span className="hidden sm:inline">Release the spacebar to hide the content. Your viewing activity is logged and tracked.</span>
                      )}
                    </div>
                    {/* Mobile reveal button */}
                    <button
                      onClick={() => {
                        if (mobileRevealTimer) {
                          // Already revealing, do nothing
                          return
                        }

                        // Start 10 second reveal
                        setIsSpacebarHeld(true)
                        setSpacebarHoldStartTime(Date.now())
                        setMobileRevealTimeLeft(10)

                        console.log('[Document Viewer] Mobile reveal started - 10 seconds')

                        // Countdown timer display
                        let timeLeft = 10
                        const countdownInterval = setInterval(() => {
                          timeLeft--
                          setMobileRevealTimeLeft(timeLeft)
                        }, 1000)

                        // Auto-hide after 10 seconds
                        const timer = setTimeout(() => {
                          setIsSpacebarHeld(false)
                          setMobileRevealTimeLeft(0)
                          clearInterval(countdownInterval)

                          if (spacebarHoldStartTime) {
                            const holdDuration = Date.now() - spacebarHoldStartTime
                            console.log(`[Document Viewer] Mobile reveal ended - content hidden (revealed for ${holdDuration}ms)`)

                            logEvent({
                              eventType: 'document_spacebar_reveal',
                              userEmail: userEmail || undefined,
                              resourceType: 'document',
                              resourceId: documentId,
                              resourceName: document?.title,
                              metadata: {
                                holdDurationMs: holdDuration,
                                revealMethod: 'mobile_button',
                                timestamp: new Date().toISOString()
                              },
                              severity: 'info'
                            })

                            setSpacebarHoldStartTime(null)
                          }
                          setMobileRevealTimer(null)
                        }, 10000)

                        setMobileRevealTimer(timer)
                      }}
                      className="sm:hidden pointer-events-auto px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold text-lg shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={mobileRevealTimer !== null}
                    >
                      {mobileRevealTimer ? `Revealing... ${mobileRevealTimeLeft}s` : 'Tap to Reveal (10s)'}
                    </button>
                  </div>
                </div>
              )}

              {/* Overlay watermark for redacted documents */}
              {document.redacted && userRole !== 'admin' && userRole !== 'owner' && (
                <div
                  className="absolute inset-0 pointer-events-none z-10"
                  style={{
                    background: 'repeating-linear-gradient(45deg, transparent, transparent 100px, rgba(251, 191, 36, 0.03) 100px, rgba(251, 191, 36, 0.03) 200px)'
                  }}
                >
                  {/* Multiple watermarks across viewport to prevent cropping */}
                  <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -rotate-45">
                    <div className="text-amber-500 opacity-20 text-5xl font-bold whitespace-nowrap">
                      REDACTED VIEWING
                    </div>
                    <div className="text-amber-600 opacity-15 text-sm text-center mt-2">
                      {viewerWatermarkText}
                    </div>
                  </div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -rotate-45">
                    <div className="text-amber-500 opacity-20 text-6xl font-bold whitespace-nowrap">
                      REDACTED VIEWING
                    </div>
                    <div className="text-amber-600 opacity-15 text-sm text-center mt-2">
                      {viewerWatermarkText}
                    </div>
                  </div>
                  <div className="absolute top-3/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -rotate-45">
                    <div className="text-amber-500 opacity-20 text-5xl font-bold whitespace-nowrap">
                      REDACTED VIEWING
                    </div>
                    <div className="text-amber-600 opacity-15 text-sm text-center mt-2">
                      {viewerWatermarkText}
                    </div>
                  </div>
                  {/* Side watermarks */}
                  <div className="absolute top-1/2 left-1/4 transform -translate-x-1/2 -translate-y-1/2 -rotate-45">
                    <div className="text-amber-500 opacity-15 text-4xl font-bold whitespace-nowrap">
                      REDACTED VIEWING
                    </div>
                  </div>
                  <div className="absolute top-1/2 right-1/4 transform translate-x-1/2 -translate-y-1/2 -rotate-45">
                    <div className="text-amber-500 opacity-15 text-4xl font-bold whitespace-nowrap">
                      REDACTED VIEWING
                    </div>
                  </div>
                </div>
              )}

              {/* PDF Embed */}
              <iframe
                src={(() => {
                  // Disable PDF toolbar for redacted documents (no print/download buttons)
                  const isExternalUser = userRole !== 'admin' && userRole !== 'owner'
                  const disableToolbar = document.redacted && isExternalUser ? '#toolbar=0' : ''
                  // Add auth token for NDA verification
                  const token = localStorage.getItem('auth-token') || ''
                  const tokenParam = token ? `&token=${encodeURIComponent(token)}` : ''
                  const url = `/api/view-document?id=${documentId}&tenantId=${sessionStorage.getItem('tenant-id') || ''}&version=${sessionStorage.getItem('content-version') || 'live'}${tokenParam}${disableToolbar}`
                  console.log('[DocumentViewPage] iframe src:', url)
                  return url
                })()}
                className="w-full h-full border-0"
                title={document.title}
                style={{
                  userSelect: 'none',
                  WebkitUserSelect: 'none'
                }}
              />
            </div>

            {/* Footer with actions */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {document.redacted && userRole !== 'admin' && userRole !== 'owner' ? (
                    <div className="flex items-center gap-3">
                      <span className={`flex items-center gap-2 ${isSpacebarHeld ? 'text-green-600 font-semibold' : 'text-amber-600'}`}>
                        {isSpacebarHeld ? '🟢 Content Visible' : '⚫ Content Hidden'} • Hold SPACE to reveal
                      </span>
                      <span className="text-gray-500">•</span>
                      <span>Download: pages {document.allowedPages || document.maxPages?.toString() || '5'} only</span>
                    </div>
                  ) : (
                    <span>Protected document • Right-click and copying disabled</span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {downloading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        {document.redacted && userRole !== 'admin' && userRole !== 'owner' ? 'Download Redacted Copy' : 'Download Full Document'}
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowViewer(false)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  )
}
